import { Loading } from "@repo/ui";
import { useEffect, useMemo } from "react";

import { useBackgroundCheckQueries } from "../../../../api/backgroundCheckApi";
import { useDocumentationApi } from "../../../../api/documentationApi";
import { useDrugTestApi } from "../../../../api/drugTestApi";
import {
	useBackgroundCheckContext,
	useDrugTestsContext,
	useRepContext,
} from "../../../../contexts/editableItemContext";
import BackgroundCheckEditorDynamic from "../../../../tabs/bgChecks/editor";
import DocumentationPage from "../../../../tabs/documentation";
import DrugTestsEditorDynamic from "../../../../tabs/drugTests/editor";
import Training from "../../../../tabs/training";
import { FullProps as Props } from "../types";
import style from "./style.module.scss";

function last<T>(arr: T[]) {
	return arr[arr.length - 1];
}

export default function ReadOnlyDynamicEditor(props: Props) {
	const { component } = props;
	const { repId } = useRepContext();
	const { overwriteItem: overwriteBCG } = useBackgroundCheckContext();
	const { overwriteItem: overwriteDrugTest } = useDrugTestsContext();
	const { useBackgroundCheckQuery } = useBackgroundCheckQueries();
	const { useDocumentationQuery } = useDocumentationApi();
	const { useDrugTestQuery } = useDrugTestApi();
	const { loaded: backgroundChecksLoaded, data: backgroundCheck } = useBackgroundCheckQuery();
	const { loaded: drugTestsLoaded, data: drugTest } = useDrugTestQuery(repId);
	const { data: documentation } = useDocumentationQuery(repId);

	const loading = useMemo(() => {
		return !backgroundChecksLoaded || !drugTestsLoaded || !documentation;
	}, [backgroundChecksLoaded, drugTestsLoaded, documentation]);

	useEffect(() => {
		if (backgroundCheck?.data) {
			overwriteBCG(last(backgroundCheck.data));
		}
	}, [backgroundCheck?.keys]);

	useEffect(() => {
		if (drugTest?.data) {
			overwriteDrugTest(last(drugTest.data));
		}
	}, [drugTest?.keys]);

	return (
		<Loading loading={loading}>
			<div className={style.vr}>{renderEditor()}</div>
		</Loading>
	);

	function renderEditor() {
		function NoInfoFound() {
			return <div>No matching information found</div>;
		}

		switch (component.label) {
			case "Background Check":
				if (!backgroundCheck || !backgroundCheck.any()) return <NoInfoFound />;
				return <BackgroundCheckEditorDynamic readOnly />;
			case "Training and Exam":
				return <Training readOnly />;
			case "Drug Test":
				if (!drugTest || !drugTest.any()) return <NoInfoFound />;
				return <DrugTestsEditorDynamic readOnly />;
			case "Performance and Standards":
			case "NRG Consent Form":
				return <DocumentationPage readOnly />;
			default:
				return <h1>Error: {component.label}</h1>;
		}
	}
}
